import { useEffect, useRef, useState } from 'react';

/** The attribute a rendered lot carries so the page can tell when it is on screen. */
export const LOT_ID_ATTR = 'data-lot-id';

/**
 * Which lots are ON SCREEN right now, top to bottom — what the Lots page
 * tells the engine (`feed.setView`) and what decides whether a bid there
 * makes a sound (Jim, 2026-10-07: sounds for lots SHOWING ON THE PAGE at the
 * time).
 *
 * Every layout marks each lot it draws with `data-lot-id`; an
 * IntersectionObserver watches them inside the element given to `ref`, and a
 * MutationObserver picks up the ones a page turn, a filter or a layout change
 * puts there. A lot drawn twice (a watchlist rail beside the grid) counts
 * once, at its first place. The answer changes only when the set does, so
 * scrolling within the same lots renders nothing.
 *
 * `ref` is a callback ref: the lots' container appears only once the sale has
 * loaded, and the observers start then.
 *
 * Without IntersectionObserver (a very old browser), every lot DRAWN counts
 * as on screen — at most one page of them, never the sale.
 */
export function useOnScreenLots(): {
  ref: (el: HTMLElement | null) => void;
  ids: readonly string[];
} {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [ids, setIds] = useState<readonly string[]>([]);
  const lastKey = useRef('');

  useEffect(() => {
    if (host === null) {
      if (lastKey.current !== '') {
        lastKey.current = '';
        setIds([]);
      }
      return;
    }

    const visible = new Set<Element>();
    let io: IntersectionObserver | null = null;

    const publish = () => {
      const seen = new Set<string>();
      const next: string[] = [];
      for (const el of host.querySelectorAll(`[${LOT_ID_ATTR}]`)) {
        const id = el.getAttribute(LOT_ID_ATTR);
        if (id === null || id === '' || seen.has(id)) continue;
        if (io !== null && !visible.has(el)) continue;
        seen.add(id);
        next.push(id);
      }
      const key = next.join('\u0001');
      if (key === lastKey.current) return;
      lastKey.current = key;
      setIds(next);
    };

    if (typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        publish();
      });
    }

    const watched = new Set<Element>();
    const sync = () => {
      const now = new Set(host.querySelectorAll(`[${LOT_ID_ATTR}]`));
      for (const el of watched) {
        if (now.has(el)) continue;
        io?.unobserve(el);
        watched.delete(el);
        visible.delete(el);
      }
      for (const el of now) {
        if (watched.has(el)) continue;
        watched.add(el);
        io?.observe(el);
      }
      publish();
    };

    const mo = new MutationObserver(sync);
    mo.observe(host, { childList: true, subtree: true });
    sync();

    return () => {
      mo.disconnect();
      io?.disconnect();
    };
  }, [host]);

  return { ref: setHost, ids };
}
